export const getApiBaseUrl = () => {
  // En production, utiliser l'URL relative (même domaine)
  // En développement, utiliser localhost:5000
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

export const getWsUrl = () => {
  const baseUrl = getApiBaseUrl();
  
  // Si l'URL est relative (commence par /), construire l'URL WebSocket à partir du domaine actuel
  if (baseUrl.startsWith('/')) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${baseUrl}${baseUrl.endsWith('/') ? '' : '/'}`;
  }
  
  const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
  const cleanBaseUrl = baseUrl.replace(/^https?:\/\//, '');
  
  return `${wsProtocol}://${cleanBaseUrl}${cleanBaseUrl.endsWith('/') ? '' : '/'}`;
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  getWsUrl() {
    const baseUrl = this.baseURL;
    
    // Si l'URL est relative (commence par /), construire l'URL WebSocket à partir du domaine actuel
    if (baseUrl.startsWith('/')) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${protocol}//${window.location.host}${baseUrl}${baseUrl.endsWith('/') ? '' : '/'}`;
    }
    
    const wsProtocol = baseUrl.startsWith('https') ? 'wss' : 'ws';
    const cleanBaseUrl = baseUrl.replace(/^https?:\/\//, '');
    
    return `${wsProtocol}://${cleanBaseUrl}${cleanBaseUrl.endsWith('/') ? '' : '/'}`;
  }

  getAuthHeader() {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Pass user's active language to the backend
      const currentLang = localStorage.getItem('i18nextLng') || 'fr';
      headers['Accept-Language'] = currentLang.split('-')[0];
      
      return headers;
    } catch (e) {
      // localStorage peut ne pas être disponible (sandbox, SSR, etc.)
      return { 'Accept-Language': 'fr' };
    }
  }

  async request(endpoint, options = {}) {
    let url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;

    // Configurer les headers
    const headers = {
      ...this.getAuthHeader(),
      ...options.headers,
    };

    // N'ajouter Content-Type: application/json QUE si ce n'est pas du FormData
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Configurer le timeout (120 secondes pour éviter les aborts prématurés)
    const timeout = options.timeout || 120000;
    let controller = null;
    let timeoutId = null;
    
    // N'utiliser AbortController que si on a un timeout explicite ou pour les requêtes longues
    if (options.signal) {
      controller = { signal: options.signal };
    } else {
      controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), timeout);
    }

    const config = {
      ...options,
      headers,
      signal: controller.signal,
    };

    // Si c'est une requête GET/DELETE sans body, retirer Content-Type
    if ((!options.body) && (options.method === 'GET' || options.method === 'DELETE')) {
      delete headers['Content-Type'];
    }

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);

      // Gérer les réponses vides
      if (response.status === 204) {
        return { success: true, data: null };
      }

      // Vérifier si la réponse est JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();

        if (!response.ok) {
          const errorMsg = data.message || `Erreur ${response.status}: ${response.statusText}`;
          console.error('❌ API Error:', errorMsg);
          const error = new Error(errorMsg);
          error.data = data;
          throw error;
        }

        return data;
      } else {
        // Si ce n'est pas du JSON
        const text = await response.text();
        console.error('❌ Response not JSON:', text.substring(0, 200));
        throw new Error(`Réponse non JSON: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ API Fetch Error:', error.message);

      // Vérifier si c'est une erreur CORS
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('Erreur de connexion au serveur. Veuillez réessayer plus tard.');
      }

      throw error;
    }
  }

  get(endpoint, params = {}) {
    let url = endpoint;

    // Gérer correctement les params
    if (params && Object.keys(params).length > 0) {
      const queryString = `?${new URLSearchParams(params).toString()}`;
      url = `${endpoint}${queryString}`;
    }

    return this.request(url, { method: 'GET' });
  }

  post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Méthode pour télécharger des fichiers
  async download(endpoint, params = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const queryString = Object.keys(params).length > 0
      ? `?${new URLSearchParams(params).toString()}`
      : '';

    const response = await fetch(`${url}${queryString}`, {
      headers: this.getAuthHeader(),
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const filename = response.headers.get('content-disposition')?.split('filename=')[1] || 'export.csv';

    // Créer un lien pour télécharger le fichier
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    return { success: true, filename };
  }

  // Méthode pour uploader des fichiers
  async upload(endpoint, formData) {
    const url = `${this.baseURL}${endpoint}`;

    const config = {
      method: 'POST',
      headers: {
        ...this.getAuthHeader(),
        // Ne pas mettre Content-Type pour FormData
      },
      body: formData,
    };

    return this.request(url, config);
  }
}

const apiService = new ApiService();

export default apiService;